import { AuthService, LoginParams, User, UserRole } from 'core/auth';
import { ChannelType, Entity } from 'core/entities';
import {
  PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  unwrapResult,
} from '@reduxjs/toolkit';

import { AppThunk } from 'store';
import { CustomError } from 'core/errors';
import { Nullable } from 'utils/types';
import { RootState } from 'store/root-reducer';
import { searchSellingCompanies } from '../selling-companies';

export type AuthStateAgentInfo = {
  salesPlace: string;
  status: string;
  description: string;
  documentNumber: string;
  lastActivityTime?: string;
};

export type AuthState = {
  isFetching: boolean;
  error: Nullable<CustomError>;
  user: Nullable<User>;
  entities: ReturnType<typeof entitiesAdapter.getInitialState>;
  debtAuthorized: boolean;
  selectedEntityId: Entity['id'];
  agentInfo: AuthStateAgentInfo;
};

// ASYNC thunks
type LoginResponse = {
  user: User;
  entities: Entity[];
  debtAuthorized: boolean;
};
export const login = createAsyncThunk<
  LoginResponse,
  LoginParams,
  { rejectValue: CustomError }
>(
  'auth/login',
  async ({ username, password }, { rejectWithValue, dispatch, getState }) => {
    try {
      const userResponse = await AuthService.login(username, password);
      await dispatch(searchSellingCompanies()).then(unwrapResult);
      const company_id = (getState() as RootState).sellingCompanies
        .currentCompany;

      if (!company_id) {
        throw CustomError.createClientError({
          technicalDescription: `CompanyID: ${company_id}`,
          description: 'No se encontró un company ID válido',
        });
      }

      return {
        user: userResponse.user,
        entities: userResponse.entities,
        debtAuthorized: userResponse.user.debtAuthorized,
      };
    } catch (err) {
      const error = err?.customError || err;
      const finalError: CustomError = error || JSON.parse(error?.stack);
      return rejectWithValue(finalError);
    }
  }
);

const entitiesAdapter = createEntityAdapter<Entity>({
  selectId: (entity) => entity.id,
  sortComparer: (a, b) => a.commercialName?.localeCompare(b.commercialName),
});

const initialState: AuthState = {
  isFetching: false,
  error: null,
  user: null,
  entities: entitiesAdapter.getInitialState(),
  selectedEntityId: '',
  debtAuthorized: false,
  agentInfo: {
    salesPlace: '',
    status: '',
    description: '',
    documentNumber: '',
    lastActivityTime: '',
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state, action: PayloadAction<undefined>) => {
      return initialState;
    },
    setAgentInfo: (state, { payload }: PayloadAction<AuthStateAgentInfo>) => {
      state.agentInfo = payload;
    },
    selectEntity: (state, { payload }: PayloadAction<Entity['id']>) => {
      const agentInfo =
        state.entities?.entities?.[payload]?.channelType === ChannelType.Direct
          ? initialState.agentInfo
          : state.agentInfo;

      state.selectedEntityId = payload;
      state.agentInfo = agentInfo;
    },
    clearError: (state, action: PayloadAction<undefined>) => {
      state.error = null;
    },
    updateSessionActivity: (state, { payload }: PayloadAction<string>) => {
      if (!!state.user) {
        state.user.lastActivityTime = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.isFetching = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      const { user, entities, debtAuthorized } = action.payload;

      state.isFetching = false;
      state.user = user;
      entitiesAdapter.setAll(state.entities, entities);
      state.debtAuthorized = debtAuthorized;
      state.selectedEntityId = '';
      state.error = null;
    });

    builder.addCase(login.rejected, (state, { payload, ...restProps }) => {
      state.error = payload;
      state.isFetching = false;
    });
  },
});

const { actions, reducer } = authSlice;

const logout = (): AppThunk => (dispatch) => {
  AuthService.logout();
  dispatch(actions.logout());
};

export const AuthActions = { ...actions, login, logout };

const entitySelectors = entitiesAdapter.getSelectors<RootState>(
  (state) => state.auth.entities
);

export const {
  selectAll: selectAllAuthEntities,
  selectTotal: selectTotalAuthEntities,
  selectById: selectAuthEntitiesById,
} = entitySelectors;

export const selectAuthLoading = (state: RootState) => state.auth.isFetching;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectLoggedUser = (state: RootState) => state.auth.user;
export const selectAgentInfo = (state: RootState) => state.auth.agentInfo;
export const selectUserRoles = (state: RootState): UserRole[] =>
  state.auth?.user?.roles || [];

export const selectUserEntity = createSelector(
  [
    (state: RootState) => state.auth.selectedEntityId,
    entitySelectors.selectEntities,
  ],
  (selectedEntityId, entities) => {
    return selectedEntityId ? entities[selectedEntityId] : null;
  }
);

export const selectAllEntitiesWithPlaceHolder = createSelector(
  [selectAllAuthEntities],
  (authEntities) =>
    authEntities.length >= 1
      ? authEntities
      : [
          {
            id: '',
            commercialName: '',
            channelType: '',
            documentNumber: '',
            isEmployee: false,
          },
        ]
);

export default reducer;
